import { useState } from 'react';

import { Dropdown, type DropdownSurface, type FieldSize, type IconName } from '@/design-system';
import { PERMISSIONS, worksInTheField } from '@/entities/user';

import { useWorkspaceAccess } from '../useWorkspaceAccess';
import { inspectorPickerCopy as copy } from './copy';
import { useInspectorChoices } from './hooks';
import { InviteInspectorPanel } from './InviteInspectorPanel';
import { inspectorOptions } from './model';
import { TakeOnRolePanel } from './TakeOnRolePanel';

export interface InspectorPickerProps {
    /** De uuid van de gekozen persoon; `''` voor niemand, of voor "alle" in een filter. */
    value: string;
    onChange: (userUuid: string) => void;
    /** Maakt er een filter van: de eerste optie ("Alle inspecteurs") heeft de waarde `''`. */
    allLabel?: string;
    placeholder?: string;
    /** Nodig buiten een `Field`. */
    'aria-label'?: string;
    surface?: DropdownSurface;
    size?: FieldSize;
    icon?: IconName;
    align?: 'start' | 'end';
    disabled?: boolean;
    className?: string;
}

/**
 * De inspecteurskeuze - dezelfde in "Nieuwe keuring", in het afspraakpaneel bij
 * een keuring en in het inspecteursfilter van de planning.
 *
 * Bovenaan staat **Mijzelf**, met je naam: in een klein keuringsbedrijf keurt de
 * eigenaar zelf. Kies je jezelf en werk je nog niet op locatie, dan vraagt de
 * keuze kort of je ook als inspecteur wilt werken. Daaronder de collega's met de
 * rol inspecteur, dan de uitnodigingen die nog openstaan
 * (uitgeschakeld: kiezen kan pas na accepteren), en onderaan - voor wie mag
 * uitnodigen (`users.invite`) - **Inspecteur uitnodigen**.
 *
 * Wie "jezelf" is, volgt `useWorkspaceAccess`: kijkt een platformbeheerder mee,
 * dan is dat de persoon op wie hij meekijkt.
 */
export function InspectorPicker({
    value,
    onChange,
    allLabel,
    placeholder,
    'aria-label': ariaLabel,
    surface,
    size,
    icon,
    align,
    disabled = false,
    className,
}: InspectorPickerProps) {
    const access = useWorkspaceAccess();
    const choices = useInspectorChoices(access);
    const [asking, setAsking] = useState(false);
    const [inviting, setInviting] = useState(false);

    const self = { uuid: access.userUuid, name: access.userName, roles: access.roles };
    const canInvite = access.can(PERMISSIONS.usersInvite);
    const options = inspectorOptions({
        self,
        members: choices.members,
        invitations: choices.invitations,
        ...(allLabel === undefined ? {} : { allLabel }),
    });

    function choose(userUuid: string) {
        onChange(userUuid);

        if (userUuid !== '' && userUuid === self.uuid && !worksInTheField(self.roles)) {
            setAsking(true);
        }
    }

    return (
        <>
            <Dropdown
                value={value}
                options={options}
                onChange={choose}
                aria-label={ariaLabel}
                placeholder={choices.loading ? copy.loading : (placeholder ?? copy.pick)}
                disabled={disabled || choices.loading}
                surface={surface}
                size={size}
                icon={icon}
                align={align}
                className={className}
                action={
                    canInvite
                        ? { label: copy.invite, icon: 'add', onSelect: () => setInviting(true) }
                        : undefined
                }
            />

            <TakeOnRolePanel open={asking} onClose={() => setAsking(false)} roles={self.roles} />

            {canInvite ? (
                <InviteInspectorPanel open={inviting} onClose={() => setInviting(false)} />
            ) : null}
        </>
    );
}
